// THE HADES BOARD'S FILTER GRAMMAR.
//
// Ported from the Work tab rather than re-invented — see
// kaironlibrary app_modules/support/staff_workspace/work_list/OVERVIEW.txt,
// "THE FILTER GRAMMAR". Every field declares a `kind`, and the kind picks both
// the control and the read path:
//
//   set    any_of                        array value
//   date   on_or_after / on_or_before    scalar value; two bounds are a range
//   count  at least / at most            scalar value
//
// `count` is the third kind in that grammar. No Hades field needs one, so it is
// absent here rather than stubbed; adding it back is one entry in the list plus
// one branch in rowMatchesFilters().
//
// THE KIND IS NOT DECORATION. A scalar must never be read through the array
// accessor — a bare string has `.length`, so it would be silently accepted and
// then mis-compared. Keeping the two read paths separate is the hard-won rule
// from the Work tab and it is kept here.
//
// `param` is the field's URL key — `<param>` for a set, `<param>_from` /
// `<param>_to` for the two bounds of a date range.
//
// ONE DEFINITION DRIVES EVERYTHING: each dropdown's option list, each option's
// count, the fold inside Support_Hades.scopedTickets, the chips, and the one
// watcher that drops a selection whose option set has gone away. Adding a fifth
// filter is a new entry in this list — not a new computed plus a new watcher
// plus a manual count fix.
import { z } from 'zod';
import {
  STATUS_LABELS, calendarDayValue, daysUntil, matchesSearch, priorityToCode,
} from '../support_constants';
import { ATTENTION_LABELS, label } from './hades_constants';

// The two creator_name values that are buckets, not people. Demo Mode masks
// every other creator (a requester's name or email) but leaves these alone:
// spoofing 'Cerberus' into a fake person would misstate who filed the row.
export const CREATOR_BUCKETS = new Set(['Cerberus', 'Unattributed']);

// Whether a creator_name value identifies a person, and so gets Demo Mode's
// `sensitive` tag wherever it renders.
export function isPersonCreator(value) {
  return !!value && !CREATOR_BUCKETS.has(value);
}

// The bucket for a row with no Zone ancestor. A real option, not a null, so the
// unzoned backlog is filterable — on day one it is most of the board, and
// sorting it into zones is the Zones tab's to-do list.
export const UNZONED = 'Unzoned';

export const HADES_FILTERS = [
  {
    key: 'zone_name',
    param: 'zone',
    kind: 'set',
    label: 'Zone',
    // Resolved server-side from the parent chain (hades_views.zone_lookup), so
    // the option list is whatever zones exist as requirements — no frontend
    // vocabulary to keep in step.
    read: (row) => row.zone_name || UNZONED,
  },
  {
    key: 'requirement_type',
    param: 'type',
    kind: 'set',
    label: 'Type',
    // Already on every row — it was rendering as 11px grey subtext under the
    // title, which is why it read as absent ("the detail table does not list
    // each type", Irene, 2026-09-03). The option list is built from the rows
    // rather than pinned to HADES_SCOPE_TYPES, so a type added to that backend
    // list appears here without a change.
    read: (row) => row.requirement_type || null,
  },
  {
    key: 'requirement_status',
    param: 'status',
    kind: 'set',
    label: 'Status',
    read: (row) => row.requirement_status || null,
    optionLabel: (value) => label(STATUS_LABELS, value),
  },
  {
    key: 'attention',
    param: 'attention',
    kind: 'set',
    label: 'Attention',
    // Un-investigated rows have no attention value, so they read as null: they
    // are not an option, and selecting any attention value excludes them. That
    // is correct — "not started vs in progress" is a question about rows Hades
    // has actually looked at.
    read: (row) => (row.investigated ? row.attention || null : null),
    optionLabel: (value) => label(ATTENTION_LABELS, value),
  },
  {
    key: 'creator_name',
    param: 'creator',
    kind: 'set',
    label: 'Created by',
    // 'Unattributed' is a real bucket, not a null: the backend collapses rows
    // with no requester and no cerberus source onto it (hades_creator_name), so
    // it is a filterable answer rather than an absence.
    read: (row) => row.creator_name || 'Unattributed',
    // Option values are requester names/emails: tag them for Demo Mode.
    sensitive: isPersonCreator,
  },
  {
    key: 'requirement_due_date',
    param: 'due',
    kind: 'date',
    label: 'Due',
    // A REAL RANGE, not an Overdue boolean — that is what the `kind` buys.
    // requirement_due_date is a Django DateField, so it arrives as a bare
    // YYYY-MM-DD; calendarDayValue() reads it as a local calendar day and never
    // as UTC midnight. See the comparison in rowMatchesFilters().
    read: (row) => calendarDayValue(row.requirement_due_date),
  },
];

const BY_KEY = Object.fromEntries(HADES_FILTERS.map((def) => [def.key, def]));

export function filterDef(key) {
  return BY_KEY[key];
}

// An unset value for one filter: [] for a set, both bounds null for a date.
export function emptyFilterValue(key) {
  return BY_KEY[key]?.kind === 'date'
    ? { on_or_after: null, on_or_before: null }
    : [];
}

export function emptyFilterState() {
  return Object.fromEntries(
    HADES_FILTERS.map((def) => [def.key, emptyFilterValue(def.key)]));
}

export function hasSelection(def, state) {
  const value = state?.[def.key];
  if (def.kind === 'date') return !!(value?.on_or_after || value?.on_or_before);
  return !!value?.length;
}

export function anySelection(state) {
  return HADES_FILTERS.some((def) => hasSelection(def, state));
}

// Every declared filter, applied to one row. THIS IS THE ONLY PLACE A FILTER IS
// EVALUATED — the rail counts and the table both reach it through
// Support_Hades.scopedTickets, so a rail total cannot disagree with the rows
// beside it.
export function rowMatchesFilters(row, state) {
  for (const def of HADES_FILTERS) {
    if (!hasSelection(def, state)) continue;
    const value = state[def.key];

    if (def.kind === 'date') {
      // BOTH SIDES FLATTEN TO A LOCAL CALENDAR DAY. requirement_due_date is a
      // bare YYYY-MM-DD and the bound comes from a date input, so neither may
      // be handed to `new Date()` — that parses a bare day as UTC midnight and
      // makes an item due TODAY read as overdue for everyone west of UTC. This
      // bug has shipped twice in this module (#2053, #2237); calendarDayValue()
      // is the fixed comparison and is pinned by support_constants.test.js.
      const after = calendarDayValue(value.on_or_after);
      const before = calendarDayValue(value.on_or_before);
      const day = def.read(row);
      // A row with no date cannot satisfy a bound, so "due on or before Friday"
      // never returns the undated backlog.
      if (day === null) return false;
      if (after !== null && day < after) return false;
      if (before !== null && day > before) return false;
      continue;
    }

    if (!value.includes(def.read(row))) return false;
  }
  return true;
}

// The values a set filter can take, ORDERED BY VOLUME, NOT ALPHABETICALLY.
// Alphabetical buries Cerberus and Unattributed — the two largest filers on the
// board and the whole reason to open the dropdown — under 46 individuals.
//
// THE OPTION SET AND THE COUNT COME FROM DIFFERENT ROW SETS, deliberately.
// `rows` is everything the customer pill admits, so the list does not shrink as
// the other filters narrow. A shrinking list would fight the stale-selection
// watcher: a selection whose option vanished gets dropped, which widens the
// rows, which brings the option back. `countRows` has every OTHER filter
// applied, so the number still says what picking this option would give you. A
// zero is a legitimate answer and reads as "nothing here", not as a missing
// control.
export function filterOptions(def, rows, countRows) {
  const counts = new Map();
  for (const row of countRows) {
    const value = def.read(row);
    if (value === null || value === undefined || value === '') continue;
    counts.set(value, (counts.get(value) || 0) + 1);
  }
  const present = new Set();
  for (const row of rows) {
    const value = def.read(row);
    if (value === null || value === undefined || value === '') continue;
    present.add(value);
  }
  return [...present]
    .map((value) => ({
      value,
      count: counts.get(value) || 0,
      label: def.optionLabel ? def.optionLabel(value) : String(value),
    }))
    // Volume first; label breaks the tie so the order is total and a re-render
    // cannot reshuffle two options that happen to have the same count.
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}

// SEARCH REUSES matchesSearch() FROM support_constants — the same function
// Support_List and Support_ServiceDesk search with; Hades becomes the third
// caller rather than the second implementation. Two things it brings that a
// substring scan does not:
//
//   - a bare numeric query ('4912' or '#4912') matches ONLY that id. That is
//     precisely the gesture the 2026-09-03 blitz was performing out loud for an
//     hour — 3589, 5412, 4873, 4846, 4912 — against a board with no search box.
//   - it returns a RANK, so an id or title hit outranks a hit in the verdict
//     narrative and results come back relevance-ordered, not merely filtered.
//
// Hades rows carry a different shape (requirement / requirement_title / story),
// so the adaptation happens here at the call site rather than by forking the
// function. `story` is the Hades verdict narrative — searchable and the only
// place a phrase like "waiting on Vytalize" actually appears.
export function searchRows(rows, query) {
  const q = (query || '').trim();
  if (!q) return rows;
  const ranked = [];
  for (const row of rows) {
    const rank = matchesSearch({
      id: row.requirement,
      title: row.requirement_title,
      description: row.story,
    }, q);
    if (rank !== null) ranked.push({ row, rank });
  }
  // Ranked on a fresh array, never on `rows` — that is a computed's source in
  // the caller, and sorting it in place is an infinite update loop rather than
  // a wrong order. Array.prototype.sort is stable, so rows keep their incoming
  // order inside a rank band.
  ranked.sort((a, b) => a.rank - b.rank);
  return ranked.map((entry) => entry.row);
}

// ---------------------------------------------------------------------------
// URL and localStorage.
//
// Both are derived from the same definitions, so a new filter becomes
// shareable and persistent without touching either encoder. Two rules from the
// Work tab carry over verbatim:
//
//   - A URL carrying ANY recognised filter key wins over localStorage entirely,
//     so a shared link reproduces exactly what the sender saw rather than a
//     merge of their view and the reader's saved one. FILTER_QUERY_KEYS is what
//     "recognised" means; the board adds its own keys to it.
//   - Restore filter arrays WHOLESALE, including an empty array. A naive
//     `if (saved.length)` silently re-adds a default the user deliberately
//     cleared.
// ---------------------------------------------------------------------------

export const FILTER_QUERY_KEYS = HADES_FILTERS.flatMap((def) => (def.kind === 'date'
  ? [`${def.param}_from`, `${def.param}_to`]
  : [def.param]));

export function filtersToQuery(state) {
  const query = {};
  for (const def of HADES_FILTERS) {
    if (!hasSelection(def, state)) continue;
    const value = state[def.key];
    if (def.kind === 'date') {
      if (value.on_or_after) query[`${def.param}_from`] = value.on_or_after;
      if (value.on_or_before) query[`${def.param}_to`] = value.on_or_before;
      continue;
    }
    // Comma-joined, matching Support_List's encoding. Every set filter here
    // holds backend enum tokens or a person's display name, none of which carry
    // a comma; a value that did would split on read.
    query[def.param] = value.join(',');
  }
  return query;
}

export function filtersFromQuery(query) {
  const state = emptyFilterState();
  // vue-router hands back an array when a key appears twice. Take the first
  // rather than joining, so a duplicated key is a malformed link, not a filter
  // that quietly means something else.
  const one = (value) => (Array.isArray(value) ? value[0] : value) || '';
  for (const def of HADES_FILTERS) {
    if (def.kind === 'date') {
      state[def.key] = {
        on_or_after: one(query[`${def.param}_from`]) || null,
        on_or_before: one(query[`${def.param}_to`]) || null,
      };
      continue;
    }
    // No numeric coercion: every set filter on this board reads a STRING off
    // the row (a type name, a status token, an attention token, a display
    // name), so a value round-trips through the URL unchanged. Support_List
    // needs parseNum because its owner/type filters hold ids.
    const raw = one(query[def.param]);
    state[def.key] = raw ? raw.split(',').filter(Boolean) : [];
  }
  return state;
}

// Zod schema for the filter half of the stored board state, built from the same
// definitions. Every field .catch()es to its unset value, so a stored filter
// whose shape has since changed degrades to "not selected" rather than throwing
// on the read that happens before the first fetch resolves.
export const FILTER_STATE_SCHEMA = z.object(Object.fromEntries(
  HADES_FILTERS.map((def) => [
    def.key,
    def.kind === 'date'
      ? z.object({
        on_or_after: z.string().nullable().catch(null),
        on_or_before: z.string().nullable().catch(null),
      }).catch(() => ({ on_or_after: null, on_or_before: null }))
      : z.array(z.string()).catch(() => []),
  ]),
// EVERY .catch() ABOVE AND BELOW IS A FACTORY, not a literal. Zod hands back
// the value it was given, so `.catch([])` returns the SAME array instance on
// every parse and one in-place edit would leak into the next restore.
)).catch(() => emptyFilterState());

// ---------------------------------------------------------------------------
// Quick filters.
//
// SHORTHANDS OVER THE SAME PIPELINE, not a parallel one — they fold in beside
// the declared filters in Support_Hades.runPipeline(), so the rail counts
// follow them like everything else. Same five-slot shape as the Work tab's
// All / My Open / Unassigned / Overdue, read for a board about neglected work.
// ---------------------------------------------------------------------------
export const QUICK_FILTERS = [
  {
    key: 'mine',
    label: 'Mine',
    // Joins on the owner's DISPLAY NAME because that is all a portfolio row
    // carries — there is no owner id on it. The caller passes the name; with
    // none it matches nothing, and Support_Hades does not offer the control.
    match: (row, ctx) => !!ctx?.userName && row.owner_name === ctx.userName,
  },
  {
    key: 'unowned',
    label: 'Unowned',
    // 81 of 404 rows. The interesting state on a board about neglected work.
    match: (row) => !row.owner_name,
  },
  {
    key: 'overdue',
    label: 'Overdue',
    // daysUntil() normalises both sides to LOCAL midnight and is DST-safe, so
    // an item due TODAY comes back 0 and is NOT overdue. Comparing a bare
    // YYYY-MM-DD against a local midnight by hand is the bug this module has
    // shipped twice (#2053, #2237). A missing or unparseable date returns null,
    // and `null < 0` is false, so it is never overdue either.
    match: (row) => daysUntil(row.requirement_due_date) < 0,
  },
  {
    key: 'needs_reply',
    label: 'Needs reply',
    // Filed by a client user, and EITHER the customer has spoken since we last
    // did (awaiting_reply — owed now, whatever the priority) OR the next Kairon
    // reply is overdue by the priority ladder (p0 same day, p1 next business
    // day, p2 five, p3/none ten) counted from our last customer-visible reply,
    // or from filing when there has been none. response_due_at is an instant,
    // not a bare calendar day, so Date.parse is the right comparison here.
    match: (row) => !!row.customer_submitted
      && (!!row.awaiting_reply || Date.parse(row.response_due_at) < Date.now()),
  },
  {
    key: 'p0_p1',
    label: 'P0-P1',
    // Through the canonical fold, so the 76 rows still holding the pre-0664
    // 'urgent' / 'high' are in it.
    match: (row) => ['p0', 'p1'].includes(priorityToCode(row.requirement_priority)),
  },
];

export const QUICK_FILTER_KEYS = QUICK_FILTERS.map((q) => q.key);

export function quickFilterAdmits(row, key, ctx) {
  if (!key) return true;
  const quick = QUICK_FILTERS.find((q) => q.key === key);
  // An unrecognised key admits nothing rather than everything: a stored or
  // link-supplied value that silently means "no filter" is a control claiming
  // to be on while doing nothing. Support_Hades validates before storing, so
  // this only fires on a hand-edited URL.
  return quick ? quick.match(row, ctx) : false;
}
